import { Alert, Button, dialogs } from "@cafe/ui";
import { useTranslation } from "react-i18next";
import { ApiError, errorMessage } from "@/shared/api/client";
import i18n from "@/shared/i18n";
import { featureLabel } from "@/shared/lib/options";

/** Ответ «так нельзя» (4xx) — причина понятна из текста: без «Повторить» и кода запроса. */
const isUserError = (e: unknown) => e instanceof ApiError && e.status >= 400 && e.status < 500 && ![401, 408, 429].includes(e.status);

/**
 * Ошибка загрузки: понятная причина (`errorMessage`). Сбой сервера/сети — «Повторить» + requestId для поддержки (ТЗ 12 §2);
 * ошибка из-за данных (4xx) — предупреждение без кода.
 */
export function QueryError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const user = isUserError(error);
  const requestId = !user && error instanceof ApiError ? error.requestId : undefined;
  return (
    <Alert tone={user ? "warning" : "danger"} action={!user && onRetry && <Button size="sm" variant="outline" onClick={onRetry}>{t("common.retry")}</Button>}>
      {errorMessage(error)}
      {requestId && (
        <button type="button" className="ms-2 text-xs text-muted underline" onClick={() => void navigator.clipboard?.writeText(requestId)}>
          {t("errors.code", { id: requestId.slice(0, 16) })}
        </button>
      )}
    </Alert>
  );
}

/**
 * Общая обработка ошибок мутаций, не разложенных по полям:
 * 402 — предложение сменить тариф, 409 VERSION_CONFLICT — данные устарели, остальное — alert.
 */
export async function handleMutationError(e: unknown, opts: { onConflict?: () => void } = {}) {
  if (e instanceof ApiError) {
    if (e.code === "PLAN_LIMIT_REACHED") {
      const d = e.details as { limit?: string; max?: number } | undefined;
      await dialogs.alert({ tone: "warning", title: i18n.t("errors.limitTitle"), description: i18n.t("errors.limitHint", { max: d?.max ?? "—" }) });
      return;
    }
    if (e.code === "FEATURE_NOT_IN_PLAN" || e.code === "SUBSCRIPTION_READ_ONLY" || e.code === "SUBSCRIPTION_SUSPENDED") {
      const feature = (e.details as { feature?: string } | undefined)?.feature;
      const title = e.code === "FEATURE_NOT_IN_PLAN" && feature ? i18n.t("guards.featureLocked", { feature: featureLabel(feature) }) : errorMessage(e);
      await dialogs.alert({ tone: "warning", title, description: i18n.t("errors.planHint") });
      return;
    }
    if (e.code === "VERSION_CONFLICT") {
      await dialogs.alert({ tone: "warning", title: i18n.t("errors.conflictTitle"), description: i18n.t("errors.conflictHint") });
      opts.onConflict?.();
      return;
    }
  }
  await dialogs.alert({ tone: "danger", title: i18n.t("errors.actionFailed"), description: errorMessage(e) });
}
